import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserGroupService } from '../../usergroup.service';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';

export interface DataPermissionSection {
  data_permission_id: number;
  wfm_system_function_id: number | null;
  section_code: string;
  section_name: string;
  display_name?: string;
  icon: string;
  icon_color: string;
  sort_order: number;
  is_view_applicable?: number;
  is_edit_applicable?: boolean;
  can_view: boolean;
  can_edit: boolean;
}

@Component({
  selector: 'app-data-permission-dialog',
  templateUrl: './data-permission-dialog.component.html',
  styleUrl: './data-permission-dialog.component.scss',
  standalone: false
})
export class DataPermissionDialogComponent implements OnInit {
  isLoading: boolean = false;
  isSaving: boolean = false;
  dataSections: DataPermissionSection[] = [];

  selectAllChecked: boolean = false;
  selectAllIndeterminate: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<DataPermissionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { userGroupId: number; functionId: number; functionName: string },
    private userGroupService: UserGroupService,
    private snackBarService: SnackBarService
  ) { }

  ngOnInit(): void {
    this.loadDataPermissions();
  }

  loadDataPermissions(): void {
    if (!this.data.userGroupId || !this.data.functionId) {
      return;
    }

    this.isLoading = true;
    this.userGroupService.getWfmDataPermissions(this.data.userGroupId, this.data.functionId).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response.status === 'SUCCESS' && response.data) {
          const rawPermissions = response.data.permissions || response.data.data_permissions || (Array.isArray(response.data) ? response.data : []);

          // Deduplicate by data_permission_id
          const map = new Map<number, DataPermissionSection>();
          rawPermissions.forEach((item: any) => {
            const permId = item.data_permission_id || item.id;
            const isViewApp = (item.is_view_applicable !== undefined && item.is_view_applicable !== null)
              ? Number(item.is_view_applicable)
              : 1;
            const isEditApp = (item.is_edit_applicable !== undefined && item.is_edit_applicable !== null)
              ? (item.is_edit_applicable == 1 || item.is_edit_applicable === true)
              : true;

            map.set(permId, {
              data_permission_id: permId,
              wfm_system_function_id: item.wfm_system_function_id || this.data.functionId,
              section_code: item.section_code || '',
              section_name: item.section_name || '',
              display_name: item.display_name || item.section_name || '',
              icon: item.icon || 'info',
              icon_color: item.icon_color || '#1976d2',
              sort_order: item.sort_order || 0,
              is_view_applicable: isViewApp,
              is_edit_applicable: isEditApp,
              can_view: isViewApp === 2 ? true : (isViewApp === 0 ? false : (item.can_view == 1 || item.can_view === true)),
              can_edit: item.can_edit == 1 && isEditApp
            });
          });

          this.dataSections = Array.from(map.values()).sort((a, b) => a.sort_order - b.sort_order);
          this.updateSelectAllStates();
        }
      },
      error: (err: any) => {
        this.isLoading = false;
        console.error('Error loading data permissions:', err);
      }
    });
  }

  onViewChange(section: DataPermissionSection, checked: boolean): void {
    if (section.is_view_applicable !== 1) {
      return;
    }
    section.can_view = checked;
    if (!checked) {
      section.can_edit = false;
    }
    this.updateSelectAllStates();
  }

  onEditChange(section: DataPermissionSection, checked: boolean): void {
    if (!section.is_edit_applicable) {
      return;
    }
    section.can_edit = checked;
    if (checked && section.is_view_applicable === 1) {
      section.can_view = true;
    }
    this.updateSelectAllStates();
  }

  onSelectAllChange(checked: boolean): void {
    this.dataSections.forEach(section => {
      if (section.is_view_applicable === 1) {
        section.can_view = checked;
      }
      if (section.is_edit_applicable) {
        section.can_edit = checked;
      }
    });
    this.updateSelectAllStates();
  }

  onClearAll(): void {
    this.dataSections.forEach(section => {
      if (section.is_view_applicable === 1) {
        section.can_view = false;
      }
      if (section.is_edit_applicable) {
        section.can_edit = false;
      }
    });
    this.updateSelectAllStates();
  }

  updateSelectAllStates(): void {
    if (this.dataSections.length === 0) {
      this.selectAllChecked = false;
      this.selectAllIndeterminate = false;
      return;
    }

    let allChecked = true;
    let anyChecked = false;

    this.dataSections.forEach(section => {
      const isViewChecked = (section.is_view_applicable === 1) ? section.can_view : true;
      const isEditChecked = section.is_edit_applicable ? section.can_edit : true;

      if (section.can_view || section.can_edit) {
        anyChecked = true;
      }
      if (!isViewChecked || !isEditChecked) {
        allChecked = false;
      }
    });

    this.selectAllChecked = allChecked;
    this.selectAllIndeterminate = !allChecked && anyChecked;
  }

  getIconStyles(section: DataPermissionSection): any {
    const color = section.icon_color || '#1976d2';
    return {
      'background-color': this.hexToRgba(color, 0.12),
      'color': color
    };
  }

  getMatIcon(section: DataPermissionSection): string {
    return section.icon || 'info';
  }

  private hexToRgba(hex: string, opacity: number): string {
    let c = hex.replace('#', '');
    if (c.length === 3) {
      c = c.split('').map(char => char + char).join('');
    }
    const num = parseInt(c, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSave(): void {
    if (this.isSaving) return;

    this.isSaving = true;
    const payload = this.dataSections.map(sec => ({
      data_permission_id: sec.data_permission_id,
      can_view: sec.can_view ? 1 : 0,
      can_edit: sec.is_edit_applicable ? (sec.can_edit ? 1 : 0) : 0
    }));

    this.userGroupService.saveWfmDataPermissions(this.data.userGroupId, this.data.functionId, payload).subscribe({
      next: (response: any) => {
        this.isSaving = false;
        if (response.status === 'SUCCESS') {
          this.snackBarService.openSnackBar({
            message: 'Data permissions updated successfully',
            title: 'Success!',
            type: 'success'
          });
          this.dialogRef.close(true);
        } else {
          this.snackBarService.openSnackBar({
            message: response.message || 'Failed to save data permissions',
            title: 'Failed!',
            type: 'error'
          });
        }
      },
      error: (err: any) => {
        this.isSaving = false;
        console.error('Error saving data permissions:', err);
        this.snackBarService.openSnackBar({
          message: 'Error saving data permissions',
          title: 'Error!',
          type: 'error'
        });
      }
    });
  }
}
